// src/routes/rightowner-routes.tsx
import { lazy } from 'react';
import type { RoutesProps } from './routes';
import {
  FaChartBar,
  FaMoneyBillWave,
  FaShieldAlt,
} from 'react-icons/fa';
import {
  BiCategoryAlt,
  BiEdit,
} from 'react-icons/bi';
import {
  MdAddBox,
} from 'react-icons/md';
import {
  HiDocumentReport,
} from 'react-icons/hi';
import { AiOutlineRobot } from 'react-icons/ai';

// Lazy load components
const RegisterWork = lazy(() => import('@/pages/management/RegisterWork'));
const ViewContent = lazy(() => import('@/pages/management/ViewContent'));
const ManageCatalog = lazy(() => import('@/pages/management/ManageCatalog'));
const UpdateMetadata = lazy(() => import('@/pages/management/UpdateMetadata'));
const RevenueDashboard = lazy(() => import('@/pages/management/RevenueDashboard'));
const WithdrawPayment = lazy(() => import('@/pages/management/WithdrawPayment'));
const IdentityProtection = lazy(() => import('@/pages/management/IdentityProtection'));
const ViolationDashboard = lazy(() => import('@/pages/management/ViolationDashboard'));
const ViolationMyReports = lazy(() => import('@/pages/management/ViolationMyReports'));
const ReportViolation = lazy(() => import('@/pages/management/ReportViolation'));
const ReportViolationEdit = lazy(() => import('@/pages/management/ReportViolationEdit'));
const ViolationAiScanManager = lazy(() => import('@/pages/management/ViolationAiScanManager'));
const ViolationDetail = lazy(() => import('@/pages/management/ViolationDetail'));
const ViolationAiScan = lazy(() => import('@/pages/management/ViolationAiScan'));

// Right Owner routes
export const rightOwnerRoutes: RoutesProps[] = [
  // Content Management
  {
    path: "/content/create",
    element: <RegisterWork />,
    name: "Đăng ký tác phẩm mới",
    roles: ["right_owner"],
    icon: MdAddBox,
  },
  {
    path: "/catalog/view",
    element: <ViewContent />,
    name: "Chi tiết tác phẩm",
    roles: ["right_owner"],
    icon: BiCategoryAlt,
  },
  {
    path: "/catalog/manage",
    element: <ManageCatalog />,
    name: "Quản lý danh mục",
    roles: ["right_owner"],
    icon: BiCategoryAlt,
  },
  {
    path: "/catalog/metadata",
    element: <UpdateMetadata />,
    name: "Cập nhật metadata",
    roles: ["right_owner"],
    icon: BiEdit,
  },
  
  // Revenue Management
  {
    path: "/rights/revenue-dashboard",
    element: <RevenueDashboard />,
    name: "Thống kê doanh thu",
    roles: ["right_owner"],
    icon: FaChartBar,
  },
  {
    path: "/rights/payments/withdraw",
    element: <WithdrawPayment />,
    name: "Rút tiền từ bản quyền",
    roles: ["right_owner"],
    icon: FaMoneyBillWave,
  },

  // Violation Management
  {
    path: "/rights/violation-dashboard",
    name: "Quản lý báo cáo vi phạm",
    element: <ViolationDashboard />,
    roles: ["right_owner", "admin", "legal"],
    icon: FaChartBar,
  },
  {
    path: "/rights/my-reports",
    name: "Báo cáo của tôi",
    element: <ViolationMyReports />,
    roles: ["right_owner"],
    icon: HiDocumentReport,
  },
  {
    path: "/violations/report",
    name: "Báo cáo Vi phạm",
    element: <ReportViolation />,
    roles: ["right_owner"],
    icon: MdAddBox,
  },
  {
    path: "/violations/report/:id/edit",
    name: "Chỉnh sửa Báo cáo",
    element: <ReportViolationEdit />,
    roles: ["right_owner"],
  },
  {
    path: "/rights/ai-scan/:sessionId",
    name: "Chi tiết AI Scan",
    element: <ViolationAiScanManager />,
    roles: ["right_owner", "admin", "monitor"],
  },
  {
    path: "/violations/:id",
    element: <ViolationDetail />,
    name: "Chi tiết báo cáo vi phạm",
    roles: ["right_owner", "admin", "legal"],
  },
  {
    path: "/monitoring/ai-scan",
    element: <ViolationAiScan />,
    name: "Quét vi phạm bằng AI",
    roles: ["monitor", "admin", "right_owner"],
    icon: AiOutlineRobot,
  },

  // Identity Protection
  {
    path: "/identity-protection/dashboard",
    element: <IdentityProtection />,
    name: "Trung tâm bảo vệ danh tính",
    roles: ["right_owner"],
    icon: FaShieldAlt,
  },
];

// Navigation structure for right owner features
export const rightOwnerNavigationStructure = {
  contentManagement: {
    key: 'content-management',
    translationKey: 'contentManagement',
    icon: BiCategoryAlt,
    roles: ['right_owner'],
    children: [
      {
        key: 'create-work',
        translationKey: 'createWork',
        path: '/content/create',
        icon: MdAddBox,
        roles: ['right_owner'],
      },
      {
        key: 'manage-catalog',
        translationKey: 'manageCatalog',
        path: '/catalog/manage',
        icon: BiCategoryAlt,
        roles: ['right_owner'],
      },
      {
        key: 'update-metadata',
        translationKey: 'updateMetadata',
        path: '/catalog/metadata',
        icon: BiEdit,
        roles: ['right_owner'],
      },
    ],
  },
  revenueManagement: {
    key: 'revenue-management',
    translationKey: 'revenueManagement',
    icon: FaChartBar,
    roles: ['right_owner'],
    children: [
      {
        key: 'revenue-dashboard',
        translationKey: 'revenueDashboard',
        path: '/rights/revenue-dashboard',
        icon: FaChartBar,
        roles: ['right_owner'],
      },
      {
        key: 'withdraw-payments',
        translationKey: 'withdrawPayments',
        path: '/rights/payments/withdraw',
        icon: FaMoneyBillWave,
        roles: ['right_owner'],
      },
    ],
  },
  violationManagement: {
    key: 'violation-management',
    translationKey: 'violationManagement',
    icon: FaChartBar,
    roles: ['right_owner'],
    children: [
      {
        key: 'violation-dashboard',
        translationKey: 'violationDashboard',
        path: '/rights/violation-dashboard',
        icon: FaChartBar,
        roles: ['right_owner', 'admin', 'legal'],
      },
      {
        key: 'my-reports',
        translationKey: 'myReports',
        path: '/rights/my-reports',
        icon: HiDocumentReport,
        roles: ['right_owner'],
      },
      {
        key: 'report-violation',
        translationKey: 'reportViolation',
        path: '/violations/report',
        icon: MdAddBox,
        roles: ['right_owner'],
      },
      {
        key: 'ai-scan',
        translationKey: 'aiScan',
        path: '/monitoring/ai-scan',
        icon: AiOutlineRobot,
        roles: ['right_owner', 'admin', 'monitor'],
      },
    ],
  },
  identityProtection: {
    key: 'identity-protection',
    translationKey: 'identityProtection',
    path: '/identity-protection/dashboard',
    icon: FaShieldAlt,
    roles: ['right_owner'],
  },
};

// Helper functions
export const getRightOwnerRoutesForRole = (userRoles: string[]): RoutesProps[] => {
  return rightOwnerRoutes.filter((route) => {
    if (!route.roles || route.roles.length === 0) return true;
    return route.roles.some((role) => userRoles.includes(role));
  });
};

export const hasAccessToRightOwnerFeatures = (userRoles: string[]): boolean => {
  return userRoles.includes('right_owner');
};

export default rightOwnerRoutes;